/**
 * 郵便番号と電話番号の形式（ご契約者の連絡先。Implem.Portal.Backend の docs/features/31 §2）。
 * <b>Implem.Portal.Backend の <c>BillingFormats</c>
 * （<c>src/Implem.Portal.Core/Tenants/BillingFormats.cs</c>）の写し</b>で、判定・揃え方・文言を同じにする。
 * <b>Backend を変えたら、ここも同じに直す</b>——直さないと、画面が通した値を API が断る（またはその逆）。
 *
 * 全角の数字・ハイフン・括弧は受けて半角にする。ハイフンに見える文字（長音「ー」・全角「－」・マイナス「−」ほか）もハイフンとして受ける。
 */

/** 郵便番号の形式が違うときの断り。 */
export const postalCodeMessage = "郵便番号は 7 桁の数字で入力してください。";

/** 電話番号の形式が違うときの断り。 */
export const telMessage = "お電話番号は 0 から始まる 10〜11 桁の数字で入力してください。";

/** ハイフンとして受ける文字。 */
const dashes = "-‐‑‒–—―−ー－ｰ";

const isDigit = (c: string | undefined): boolean => c !== undefined && c >= "0" && c <= "9";

/** 半角の数字だけか。 */
const allDigits = (text: string): boolean => /^\d*$/u.test(text);

/** 全角の数字・ハイフン・括弧を半角にし、前後の空白を落とす。 */
const toHalfWidth = (value: string): string =>
    Array.from(value.trim(), (c) => {
        if (c >= "０" && c <= "９") {
            return String.fromCodePoint((c.codePointAt(0) ?? 0) - 0xFE_E0);
        }
        if (c === "（") {
            return "(";
        }
        if (c === "）") {
            return ")";
        }
        return dashes.includes(c) ? "-" : c;
    }).join("");

/** 郵便番号を <c>123-4567</c> の形に揃える。形式が違えば undefined（数字 7 桁。ハイフンは 3 桁目の後に 1 つだけ、有っても無くてもよい）。 */
export const normalizePostalCode = (value: string): string | undefined => {
    const text = toHalfWidth(value);
    const digits = text.replaceAll("-", "");
    const shaped = text.length === 7 || (text.length === 8 && text[3] === "-");

    return shaped && digits.length === 7 && allDigits(digits)
        ? `${digits.slice(0, 3)}-${digits.slice(3)}`
        : undefined;
};

/**
 * 電話番号を半角に揃える。形式が違えば undefined。
 * 数字・ハイフン・括弧だけで、数字が 10～11 桁・最初の数字が 0。先頭と末尾は数字（先頭だけは括弧も可）、区切りは続けない。
 * <b>区切りの位置は入力のまま</b>（市外局番の桁数は地域で違う）。国際番号（+81）と空白は受けない。
 */
export const normalizeTel = (value: string): string | undefined => {
    const text = toHalfWidth(value);
    // ここから先は半角の数字・ハイフン・括弧だけ（1 文字 1 符号単位）
    if (!/^[\d()-]+$/u.test(text)) {
        return undefined;
    }

    const digits = text.replaceAll(/\D/gu, "").length;
    const startsWell = text.startsWith("0") || text.startsWith("(0");
    const endsWell = isDigit(text.at(-1));
    const repeated = /--|\(\(|\)\)/u.test(text);

    return startsWell && endsWell && !repeated && (digits === 10 || digits === 11) ? text : undefined;
};
